/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import React from 'react'
import Text from './Text'
import Card from './Card'
import Icon from './Icon'

/**
 * Gondel-Session-Karte für die Übersicht
 * @param {Object} props
 * @param {string} props.title - Gondel-Thema
 * @param {{ fullName: string, company: string }[]} [props.speakers] - Speaker-Daten
 * @param {string} props.time - Uhrzeit (z.B. "14:00")
 * @param {number} props.availableSlots - Verfügbare öffentliche Plätze
 * @param {boolean} props.soldOut - Ausgebucht
 * @param {boolean} [props.booked] - User hat bereits gebucht
 * @param {string} [props.bookingStatus] - Booking status ('confirmed'|'checked-in')
 * @param {boolean} [props.cancelled] - Gondel storniert
 * @param {Function} props.onClick - Klick-Handler
 */
export default function GondolaCard({
  title,
  speakers = [],
  time,
  date,
  availableSlots,
  soldOut = false,
  booked = false,
  bookingStatus,
  cancelled = false,
  onClick,
}) {
  const disabled = (soldOut || cancelled) && !booked

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={[
        'min-h-[48px] rounded-lg transition-all duration-200',
        disabled ? 'opacity-50 cursor-default' : 'cursor-pointer hover:shadow-md',
      ].join(' ')}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick?.()
        }
      }}
    >
      <Card variant="elevated" className="border-l-2 border-l-eggs-yellow">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            {(date || time) && (
              <Text type="Caption" variant="sm" weight="Strong" className="text-eggs-yellow mb-0.5">
                {[date, time ? `${time} Uhr` : ''].filter(Boolean).join(' · ')}
              </Text>
            )}
            <Text type="Heading" variant="h4" className="mb-1">
              {title}
            </Text>
            {speakers.length > 0 && (
              <Text type="Body" variant="sm" className="text-gray-600">
                {speakers.map((s) => s.fullName).join(' & ')}
              </Text>
            )}
          </div>
          <div className="text-right shrink-0">
            {booked ? (
              bookingStatus === 'checked-in' ? (
                <Text type="Caption" variant="xs" className="inline-flex items-center gap-1 px-2 py-1 font-medium bg-status-experienced-bg text-status-experienced-text rounded">
                  <Icon name="award" size={14} color="#7c3aed" />
                  Erlebt
                </Text>
              ) : (
                <Text type="Caption" variant="xs" className="inline-flex items-center gap-1 px-2 py-1 font-medium bg-status-booked-bg text-status-booked-text rounded">
                  <Icon name="check-circle" size={14} color="#16a34a" />
                  Gebucht
                </Text>
              )
            ) : cancelled ? (
              <Text type="Caption" variant="xs" className="inline-block px-2 py-1 font-medium bg-gray-100 text-gray-600 rounded">
                ⚠️ Storniert
              </Text>
            ) : soldOut ? (
              <Text type="Caption" variant="xs" className="inline-block px-2 py-1 font-medium bg-red-50 text-red-800 rounded">
                Ausgebucht
              </Text>
            ) : (
              <Text type="Body" variant="sm" className="text-green-800 font-medium">
                {availableSlots} {availableSlots === 1 ? 'Platz' : 'Plätze'} frei
              </Text>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
